import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

export type ConfiscationType =
  | "fund_confiscation"
  | "other_debit"
  | "balance_transfer_debit"
  | ""
  | null;

export interface ConfiscationItem {
  operationId: number;
  operationPublicId?: string;
  userId: number;
  playerNickname?: string | null;
  playerEmail?: string | null;
  operatorName?: string | null;
  operatorEmail?: string | null;
  confiscationType?: ConfiscationType;
  amount: string;
  amountMinor?: number;
  currency: string;
  currencyScale?: number;
  statusCode: string;
  transactionCode?: string | null;
  internalComment?: string | null;
  operatorId?: number | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface ConfiscationOrder {
  id: "asc" | "desc";
}

export interface GetConfiscationsPayload {
  limit: number;
  offset: number;
  order: ConfiscationOrder;
  userIds?: number[];
  playerNickname?: string;
  operatorIds?: number[];
  confiscationTypes?: string[];
  transactionCode?: string;
  currencies?: string[];
  amountFrom?: string;
  amountTo?: string;
  createdFrom?: string;
  createdTo?: string;
}

interface PaymentsConfiscationsState {
  list: ConfiscationItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  hasListError: boolean;
}

const initialState: PaymentsConfiscationsState = {
  list: [],
  activePage: 1,
  totalPages: 0,
  totalItems: 0,
  isListFetched: false,
  hasListError: false,
};

export const getConfiscationsList = createAsyncThunk(
  "paymentsConfiscations/list",
  async (payload: GetConfiscationsPayload) => {
    const result = await instance.post(
      apiRoutes.paymentConfiscationsList,
      payload,
    );

    return result.data;
  },
);

export const paymentsConfiscationsSlice = createSlice({
  name: "paymentsConfiscations",
  initialState,
  reducers: {
    handleConfiscationsPaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.hasListError = false;
    },
    resetConfiscationsList: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConfiscationsList.pending, (state) => {
      state.isListFetched = false;
      state.hasListError = false;
    });
    builder.addCase(getConfiscationsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
    });
    builder.addCase(getConfiscationsList.rejected, (state) => {
      state.isListFetched = true;
      state.hasListError = true;
      state.list = [];
      state.totalItems = 0;
      state.totalPages = 0;
    });
  },
});

export const { handleConfiscationsPaginate, resetConfiscationsList } =
  paymentsConfiscationsSlice.actions;
